import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const IntroOverlay = () => {
  const [show, setShow] = useState(() => {
    try {
      return !sessionStorage.getItem("rotu_intro_seen");
    } catch (e) {
      return true;
    }
  });
  const [video, setVideo] = useState(undefined);
  const [muted, setMuted] = useState(false);
  const vidRef = useRef(null);

  // try sound-on autoplay first; browsers that block it get muted + a sound prompt
  useEffect(() => {
    if (!video || !vidRef.current) return;
    const v = vidRef.current;
    v.muted = false;
    const tryPlay = () =>
      v.play().catch(() => {
        if (!v.muted) {
          v.muted = true;
          setMuted(true);
          v.play().catch(() => {});
        }
      });
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, [video]);

  const dismiss = () => {
    try {
      sessionStorage.setItem("rotu_intro_seen", "1");
    } catch (e) {}
    window.dispatchEvent(new CustomEvent("rotu:enter"));
    setShow(false);
  };

  useEffect(() => {
    axios
      .get(`${API}/video`)
      .then((r) => setVideo(r.data.video))
      .catch(() => setVideo(null));
  }, []);

  useEffect(() => {
    if (show && video === null) {
      const id = setTimeout(dismiss, 3600);
      return () => clearTimeout(id);
    }
  }, [show, video]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="intro-overlay"
        >
          {video ? (
            <>
              <video
                ref={vidRef}
                src={`${BACKEND}${video.url}`}
                autoPlay
                muted={muted}
                playsInline
                preload="auto"
                onEnded={dismiss}
                className="absolute inset-0 w-full h-full object-cover opacity-80"
                data-testid="intro-video"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/70 pointer-events-none" />
              <div className="absolute top-8 left-8 text-[10px] tracking-[0.5em] uppercase text-zinc-500">
                Transmission // Intro film
              </div>
            </>
          ) : (
            <div className="text-center px-6">
              <div
                className="font-display font-black text-6xl sm:text-8xl text-[#39FF14] glitchy"
                data-testid="intro-title"
              >
                R.O.T.U.
              </div>
              <div className="mt-8 text-[11px] tracking-[0.5em] uppercase text-zinc-500 animate-pulse">
                Establishing connection...
              </div>
            </div>
          )}

          <div className="absolute bottom-8 right-8 flex items-center gap-3">
            {video && (
              <button
                onClick={() => {
                  const v = vidRef.current;
                  setMuted((m) => {
                    const next = !m;
                    if (v) {
                      v.muted = next;
                      if (!next && v.paused) v.play().catch(() => {});
                    }
                    return next;
                  });
                }}
                className="h-10 px-3 border border-white/25 text-zinc-300 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200 flex items-center gap-2"
                data-testid="intro-sound-toggle"
                aria-label={muted ? "Unmute intro" : "Mute intro"}
              >
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                {muted && (
                  <span className="text-[9px] tracking-[0.25em] uppercase animate-pulse" data-testid="intro-sound-hint">
                    Tap for sound
                  </span>
                )}
              </button>
            )}
            <button
              onClick={dismiss}
              className="px-6 py-3 border border-[#39FF14] text-[#39FF14] bg-black/60 backdrop-blur-md font-display text-xs font-bold uppercase tracking-widest hover:bg-[#39FF14] hover:text-black transition-colors duration-200"
              data-testid="intro-skip-button"
            >
              Enter the pit
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
